import React, { useState, useEffect, useCallback } from 'react';
import { ArrowLeft, Mail, Archive, MailOpen, Send, RefreshCw, Filter, LogOut } from 'lucide-react';
import { useAuth } from '../auth/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const fetchAuth = (url, opts = {}) => fetch(url, { credentials: 'include', ...opts });

const STATUS_LABELS = { unread: 'Unread', replied: 'Replied', archived: 'Archived' };
const STATUS_COLORS = {
  unread: 'bg-[#14B8A6]/10 text-[#14B8A6] border-[#14B8A6]/20',
  replied: 'bg-purple-500/10 text-purple-400 border-purple-500/20',
  archived: 'bg-gray-500/10 text-gray-400 border-gray-500/20',
};

const StatusBadge = ({ status }) => (
  <span className={`text-xs px-2 py-0.5 rounded border ${STATUS_COLORS[status]}`} data-font="label">
    {STATUS_LABELS[status]}
  </span>
);

const MessageRow = ({ msg, onSelect }) => (
  <button
    onClick={() => onSelect(msg)}
    className={`w-full text-left p-4 border-b border-[#3d2a5c]/10 hover:bg-[#1a0f2e]/30 transition-colors ${msg.status === 'unread' ? 'bg-[#0f0b18]/40' : ''}`}
    data-testid={`message-row-${msg.id}`}
  >
    <div className="flex items-start justify-between gap-4">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-[#F0EDE8] font-medium truncate">{msg.name}</span>
          <StatusBadge status={msg.status} />
        </div>
        <p className="text-[#E8E4DF]/60 text-sm truncate">{msg.email}</p>
        <p className="text-[#E8E4DF]/40 text-sm mt-1 truncate">{msg.message}</p>
      </div>
      <span className="text-[#E8E4DF]/30 text-xs whitespace-nowrap" data-font="label">
        {new Date(msg.created_at).toLocaleDateString()}
      </span>
    </div>
  </button>
);

const StatusActions = ({ onStatusChange }) => (
  <div className="flex gap-3" data-font="label">
    <button onClick={() => onStatusChange('unread')} className="flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-wider border border-[#3d2a5c]/20 text-[#E8E4DF]/60 hover:text-[#14B8A6] hover:border-[#14B8A6]/30 transition-colors">
      <MailOpen className="w-3 h-3" /> Mark Unread
    </button>
    <button onClick={() => onStatusChange('replied')} className="flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-wider border border-[#3d2a5c]/20 text-[#E8E4DF]/60 hover:text-purple-400 hover:border-purple-400/30 transition-colors">
      <Send className="w-3 h-3" /> Mark Replied
    </button>
    <button onClick={() => onStatusChange('archived')} className="flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-wider border border-[#3d2a5c]/20 text-[#E8E4DF]/60 hover:text-gray-400 hover:border-gray-400/30 transition-colors">
      <Archive className="w-3 h-3" /> Archive
    </button>
  </div>
);

const MessageDetail = ({ msg, onBack, onStatusChange }) => (
  <div className="p-6" data-testid="message-detail">
    <button onClick={onBack} className="flex items-center gap-2 text-[#14B8A6] hover:text-[#0D9488] mb-6 text-sm" data-font="label">
      <ArrowLeft className="w-4 h-4" /> Back to inbox
    </button>
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-medium text-[#F0EDE8]">{msg.name}</h2>
        <StatusBadge status={msg.status} />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
        <div><span className="text-[#14B8A6]">Email:</span> <span className="text-[#E8E4DF] ml-2">{msg.email}</span></div>
        <div><span className="text-[#14B8A6]">Date:</span> <span className="text-[#E8E4DF] ml-2">{new Date(msg.created_at).toLocaleString()}</span></div>
        <div><span className="text-[#14B8A6]">Subject:</span> <span className="text-[#E8E4DF] ml-2">{msg.subject || '(none)'}</span></div>
        <div><span className="text-[#14B8A6]">Source:</span> <span className="text-[#E8E4DF] ml-2">{msg.source_page}</span></div>
        <div><span className="text-[#14B8A6]">ID:</span> <span className="text-[#E8E4DF]/50 ml-2 text-xs">{msg.id}</span></div>
      </div>
      <div className="bg-[#0A0A0B] border border-[#3d2a5c]/15 p-5 rounded">
        <p className="text-[#E8E4DF] whitespace-pre-wrap">{msg.message}</p>
      </div>
      <StatusActions onStatusChange={onStatusChange} />
    </div>
  </div>
);

const FilterBar = ({ filter, onFilterChange }) => (
  <div className="flex gap-2 mb-6" data-font="label">
    {[null, 'unread', 'replied', 'archived'].map((f) => (
      <button
        key={f || 'all'}
        onClick={() => onFilterChange(f)}
        className={`px-4 py-2 text-xs uppercase tracking-wider border transition-colors ${filter === f ? 'border-[#14B8A6]/40 text-[#14B8A6] bg-[#14B8A6]/5' : 'border-[#3d2a5c]/15 text-[#E8E4DF]/40 hover:text-[#E8E4DF]/70'}`}
        data-testid={`filter-${f || 'all'}`}
      >
        <Filter className="w-3 h-3 inline mr-1" />
        {f ? STATUS_LABELS[f] : 'All'}
      </button>
    ))}
  </div>
);

const MessageList = ({ messages, loading, filter, onSelect }) => {
  if (loading) {
    return <p className="p-8 text-center text-[#E8E4DF]/40">Loading...</p>;
  }
  if (messages.length === 0) {
    return <p className="p-8 text-center text-[#E8E4DF]/40">No messages{filter ? ` with status "${filter}"` : ''}</p>;
  }
  return messages.map((msg) => <MessageRow key={msg.id} msg={msg} onSelect={onSelect} />);
};

const AdminInbox = () => {
  const { logout } = useAuth();
  const [messages, setMessages] = useState([]);
  const [total, setTotal] = useState(0);
  const [filter, setFilter] = useState(null);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchMessages = useCallback(async () => {
    setLoading(true);
    const params = filter ? `?status=${filter}` : '';
    try {
      const res = await fetchAuth(`${API_URL}/api/admin/messages${params}`);
      if (res.status === 401) {
        await logout();
        return;
      }
      const data = await res.json();
      setMessages(data.messages || []);
      setTotal(data.total || 0);
    } catch (err) {
      console.error('Failed to fetch messages:', err);
    } finally {
      setLoading(false);
    }
  }, [filter, logout]);

  useEffect(() => { fetchMessages(); }, [fetchMessages]);

  const handleStatusChange = async (newStatus) => {
    if (!selected) return;
    try {
      const res = await fetchAuth(`${API_URL}/api/admin/messages/${selected.id}/status?status=${newStatus}`, { method: 'PATCH' });
      if (res.status === 401) { await logout(); return; }
      setSelected({ ...selected, status: newStatus });
      fetchMessages();
    } catch (err) {
      console.error('Failed to update status:', err);
    }
  };

  if (selected) {
    return (
      <div className="min-h-screen bg-[#0A0A0B] pt-20">
        <div className="container mx-auto px-6 py-8 max-w-4xl">
          <MessageDetail msg={selected} onBack={() => setSelected(null)} onStatusChange={handleStatusChange} />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0B] pt-20" data-testid="admin-inbox">
      <div className="container mx-auto px-6 py-8 max-w-4xl">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-medium text-[#F0EDE8]">Contact Inbox</h1>
            <p className="text-[#E8E4DF]/50 text-sm mt-1" data-font="label">{total} messages</p>
          </div>
          <div className="flex gap-3" data-font="label">
            <button onClick={fetchMessages} className="flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-wider border border-[#3d2a5c]/20 text-[#E8E4DF]/60 hover:text-[#14B8A6] transition-colors">
              <RefreshCw className="w-3 h-3" /> Refresh
            </button>
            <button onClick={logout} className="flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-wider border border-[#3d2a5c]/20 text-[#E8E4DF]/60 hover:text-red-400 hover:border-red-400/30 transition-colors" data-testid="admin-logout-btn">
              <LogOut className="w-3 h-3" /> Sign Out
            </button>
          </div>
        </div>
        <FilterBar filter={filter} onFilterChange={setFilter} />
        <div className="border border-[#3d2a5c]/15 rounded">
          <MessageList messages={messages} loading={loading} filter={filter} onSelect={setSelected} />
        </div>
      </div>
    </div>
  );
};

export default AdminInbox;
